import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  Query,
} from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  from,
  mergeMap,
  of,
  switchMap,
  take,
} from 'rxjs';
import { Exam } from '../../models/exam.school.model';
import { UserModel } from '../../models/user.model';
import { SchoolExamService } from '../../services/exam.school.service';
import * as examActions from '../actions/exam.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class SchoolExamEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  examService: SchoolExamService = inject(SchoolExamService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);

  user: UserModel | null = null;
  userSub: Subscription | undefined;

  private logout$ = new Subject<void>();

  constructor() {
    this.userSub = this.store
      .select((state) => state.user.user)
      .pipe(take(1))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Pending Exams
  getExams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(examActions.getExams),
      debounceTime(500),
      mergeMap(({ groupId, createdById, lastRecord }) => {
        // console.log(
        //   `getExams$: groupId: ${groupId}, gradeId: ${gradeId}, createdById: ${createdById}, academicYear: ${academicYear}, lastRecord: ${lastRecord}`
        // );
        let query: AngularFirestoreCollection<Exam> = this.afs
          .collection('groups')
          .doc(groupId)
          .collection('exams', (ref) => {
            let reference = ref as Query;

            if (createdById) {
              reference = reference.where('createdById', '==', createdById);
            }
            reference = reference.orderBy('modified', 'desc');

            if (lastRecord) reference = reference.startAfter(lastRecord);

            return reference;
          });
        return query.get();
      }),
      mergeMap((exams) => {
        // console.log(`getExams$: Exams: ${exams.docs.length}`);
        const result: (Exam | null)[] = exams.docs.map((doc) => {
          try {
            const exam = doc.data() as Exam;
            exam.id = doc.id;
            // console.log(`Exam: ${JSON.stringify(exam)}`);
            return exam;
          } catch (error) {
            return null;
          }
        });
        const filtered = result.filter((value) => value !== null);
        if (filtered == null) return EMPTY;
        return of(examActions.addExamsToStore({ exams: filtered }));
      }),
      catchError((error) => {
        // console.error(`Error fetching exams: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Exams'
        );
        return of(examActions.getExamsFailure({ error }));
      })
    )
  );

  getExam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(examActions.getExam),
      debounceTime(500),
      switchMap(({ groupId, examId }) => {
        console.log(`getExam$: groupId: ${groupId}, examId: ${examId}`);
        return this.afs
          .collection('groups')
          .doc(groupId)
          .collection('exams')
          .doc(examId)
          .get();
      }),
      switchMap((doc) => {
        if (!doc.exists) {
          console.log('getExam$: Exam not found');
          return of(
            examActions.getExamFailure({
              error: {
                title: 'Exam not found',
                message:
                  'The exam could not be fetched. Please try again later.',
              },
            })
          );
        }
        try {
          const exam = doc.data() as Exam;
          exam.id = doc.id;
          console.log(`getExam$: Exam: ${JSON.stringify(exam)}`);
          return of(examActions.addExamToStore({ exam: exam }));
        } catch (error) {
          return of(
            examActions.getExamFailure({
              error: {
                title: 'Exam not found',
                message:
                  'The exam could not be fetched. Please try again later.',
              },
            })
          );
        }
      }),
      catchError((error) => {
        console.error(`Error fetching exam: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Exam'
        );
        return of(examActions.getExamsFailure({ error }));
      })
    )
  );

  // Effect to create a new exam
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(examActions.create),
      debounceTime(500),
      switchMap(({ groupId, exam }) => {
        const createExam = {
          ...exam,
          created: Timestamp.now(),
          modified: Timestamp.now(),
        };

        return from(this.examService.create(groupId, createExam)).pipe(
          switchMap((examId) => {
            const localExam: Exam = {
              ...exam,
              id: examId,
              created: Timestamp.fromDate(moment(exam.created).toDate()),
              modified: Timestamp.fromDate(moment(exam.modified).toDate()),
            };

            // Success notification and navigation
            this.toastr.success(
              'The exam has been created successfully.',
              'Exam Created'
            );
            // this.router.navigate(['school', groupId, 'exams', exam.id]);
            return of(examActions.createSuccess({ exam: localExam }));
          }),
          catchError((error) => {
            console.log(`Error creating exam: ${error.toString()}`);
            this.toastr.error(
              `An error occurred during Exam Creation. Please try again later.`,
              'Error Creating Exam'
            );
            return of(examActions.createFailure({ error }));
          })
        );
      })
    )
  );

  // Effect to update a exam
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(examActions.update),
      mergeMap(({ groupId, examId, exam }) => {
        const updateExam = {
          ...exam,
          modified: serverTimestamp() as Timestamp,
        };
        return from(this.examService.update(groupId, examId, updateExam)).pipe(
          mergeMap(() => {
            const localExam: Exam = {
              ...exam,
              id: examId,
              modified: Timestamp.fromDate(moment(exam.modified).toDate()),
            };
            this.toastr.success(
              'The exam details have been updated successfully.',
              'Exam Updated'
            );
            return of(examActions.updateSuccess({ exam: localExam }));
          }),
          catchError((error) => {
            console.error(`Error updating exam: ${error}`);
            this.toastr.error(
              `An error occurred while updating the exam details. Please try again later.`,
              'Error Updating Exam'
            );
            return of(examActions.updateFailure({ error }));
          })
        );
      })
    )
  );

  // effect to delete a exam
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(examActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.examService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The exam has been deleted successfully.',
              'Exam Deleted'
            );
            return examActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            // console.error(`Error deleting exam: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Exam'
            );
            return examActions.deleteFailure({ error });
          })
      )
    )
  );

  clearExams$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(examActions.clear());
      })
    )
  );
}
